import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CarouselComponent = ({ onChange }) => {
    const [carouselItems, setCarouselItems] = useState([]);
    const [mediaType, setMediaType] = useState('Image');

    const handleMediaTypeChange = (e) => {
        setMediaType(e.target.value);
    };

    const handleAddItem = () => {
        if (carouselItems.length >= 10) {
            toast.error('You can only add up to 10 carousel items.', { autoClose: 3000 });
            return;
        }
        setCarouselItems([
            ...carouselItems,
            { id: Date.now(), body: '', buttonText: '', domainUrl: '', quickReplyButton: '' },
        ]);
    };

    const handleInputChange = (id, field, value) => {
        const updatedItems = carouselItems.map((item) =>
            item.id === id ? { ...item, [field]: value } : item
        );
        setCarouselItems(updatedItems);
        onChange(updatedItems); // Pass updated JSON object to parent
    };

    const handleRemoveItem = (id) => {
        const updatedItems = carouselItems.filter((item) => item.id !== id);
        setCarouselItems(updatedItems);
        onChange(updatedItems); // Pass updated JSON object to parent
    };

    return (
        <div style={cs.wrap}>
            {/* Media type + Add button row */}
            <div style={cs.topRow}>
                <div style={cs.mediaTypeGroup}>
                    <span style={cs.mediaLabel}>Media Type</span>
                    {['Image', 'Video'].map(type => (
                        <label key={type} style={{ ...cs.pill, ...(mediaType === type ? cs.pillActive : {}) }}>
                            <input
                                type="radio"
                                name="mediaType"
                                id={`mediaType${type}`}
                                value={type}
                                checked={mediaType === type}
                                onChange={handleMediaTypeChange}
                                style={{ display: 'none' }}
                            />
                            <i className={`bi ${type === 'Image' ? 'bi-image' : 'bi-play-circle'}`} style={{ marginRight: 5, fontSize: 12 }}></i>
                            {type}
                        </label>
                    ))}
                </div>
                <button type="button" style={cs.addBtn} onClick={handleAddItem}>
                    <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                    Add Carousel Item
                    <span style={cs.countBadge}>{carouselItems.length}/10</span>
                </button>
            </div>

            {/* Media upload */}
            <div style={cs.uploadRow}>
                <label style={cs.uploadLabel}>
                    <i className={`bi ${mediaType === 'Image' ? 'bi-image' : 'bi-play-circle'}`} style={{ marginRight: 6, color: '#1565c0' }}></i>
                    Upload {mediaType}
                </label>
                <div style={{ flex: 1 }}>
                    <input
                        type="file"
                        style={cs.fileInput}
                        accept={mediaType === 'Image' ? 'image/*' : 'video/*'}
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file && file.size > 5 * 1024 * 1024) {
                                toast.error('File size should be less than or equal to 5MB.', { autoClose: 3000 });
                                e.target.value = '';
                            }
                        }}
                    />
                    <p style={cs.uploadHint}>
                        <i className="bi bi-info-circle" style={{ marginRight: 4 }}></i>
                        {mediaType === 'Image' ? 'JPG, JPEG, PNG' : 'MP4'} · Max 5MB
                    </p>
                </div>
            </div>

            {/* Carousel cards */}
            {carouselItems.length > 0 && (
                <div style={cs.cardsScroll}>
                    {carouselItems.map((item, index) => (
                        <div key={item.id} style={cs.card}>
                            {/* Card header */}
                            <div style={cs.cardHeader}>
                                <span style={cs.cardTitle}>
                                    <i className="bi bi-card-image" style={{ marginRight: 6, color: '#1565c0' }}></i>
                                    Item {index + 1}
                                </span>
                                <button style={cs.removeBtn} onClick={() => handleRemoveItem(item.id)} title="Remove item">
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>

                            {/* Card body */}
                            <div style={cs.cardBody}>
                                {/* Body text */}
                                <label style={cs.fieldLabel}>Body Text</label>
                                <textarea
                                    style={cs.textarea}
                                    rows="3"
                                    placeholder="Enter carousel body text…"
                                    value={item.body}
                                    onChange={(e) => handleInputChange(item.id, 'body', e.target.value)}
                                    id={`textarea-${item.id}`}
                                />
                                {/* Text formatting buttons */}
                                <div style={cs.fmtRow}>
                                    <button type="button" style={cs.fmtBtn} title="Insert emoji"
                                        onClick={() => {
                                            const ta = document.getElementById(`textarea-${item.id}`);
                                            const pos = ta.selectionStart;
                                            handleInputChange(item.id, 'body', item.body.slice(0, pos) + '😊' + item.body.slice(pos));
                                        }}>😊</button>
                                    <button type="button" style={cs.fmtBtn} title="Bold"
                                        onClick={() => {
                                            const ta = document.getElementById(`textarea-${item.id}`);
                                            const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd);
                                            if (sel) handleInputChange(item.id, 'body', ta.value.replace(sel, `**${sel}**`));
                                        }}><i className="bi bi-type-bold"></i></button>
                                    <button type="button" style={cs.fmtBtn} title="Italic"
                                        onClick={() => {
                                            const ta = document.getElementById(`textarea-${item.id}`);
                                            const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd);
                                            if (sel) handleInputChange(item.id, 'body', ta.value.replace(sel, `*${sel}*`));
                                        }}><i className="bi bi-type-italic"></i></button>
                                    <button type="button" style={cs.fmtBtn} title="Underline"
                                        onClick={() => {
                                            const ta = document.getElementById(`textarea-${item.id}`);
                                            const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd);
                                            if (sel) handleInputChange(item.id, 'body', ta.value.replace(sel, `<u>${sel}</u>`));
                                        }}><i className="bi bi-type-underline"></i></button>
                                </div>

                                {/* Button text */}
                                <label style={cs.fieldLabel}>Button Text</label>
                                <input type="text" style={cs.input} placeholder="Enter Button Text"
                                    value={item.buttonText}
                                    onChange={(e) => handleInputChange(item.id, 'buttonText', e.target.value)} />

                                {/* Domain URL */}
                                <label style={cs.fieldLabel}>Domain URL</label>
                                <input type="text" style={cs.input} placeholder="https://www.domain.com"
                                    value={item.domainUrl}
                                    onChange={(e) => handleInputChange(item.id, 'domainUrl', e.target.value)} />

                                {/* Quick Reply */}
                                <label style={cs.fieldLabel}>Quick Reply Button</label>
                                <input type="text" style={cs.input} placeholder="Enter quick reply button text"
                                    value={item.quickReplyButton}
                                    onChange={(e) => handleInputChange(item.id, 'quickReplyButton', e.target.value)} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

const cs = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 },
    topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 },
    mediaTypeGroup: { display: 'flex', alignItems: 'center', gap: 8 },
    mediaLabel: { fontSize: '13px', fontWeight: '600', color: '#374151', marginRight: 4 },
    pill: { display: 'inline-flex', alignItems: 'center', padding: '6px 14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '500', userSelect: 'none', transition: 'all 0.15s' },
    pillActive: { border: '1.5px solid #1565c0', background: '#eff6ff', color: '#1565c0' },
    addBtn: { display: 'inline-flex', alignItems: 'center', padding: '8px 14px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', gap: 6 },
    countBadge: { background: 'rgba(255,255,255,0.25)', padding: '1px 7px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' },
    uploadRow: { display: 'flex', alignItems: 'flex-start', gap: 12, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' },
    uploadLabel: { fontSize: '13px', fontWeight: '600', color: '#374151', minWidth: 100, display: 'flex', alignItems: 'center', paddingTop: 6 },
    fileInput: { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '7px', outline: 'none', boxSizing: 'border-box' },
    uploadHint: { margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' },
    cardsScroll: { display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 },
    card: { minWidth: 280, flex: '0 0 auto', background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #f3f4f6' },
    cardTitle: { fontSize: '13px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    removeBtn: { background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: '6px', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12 },
    cardBody: { padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
    fieldLabel: { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 2 },
    textarea: { width: '100%', padding: '8px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '7px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
    fmtRow: { display: 'flex', gap: 4 },
    fmtBtn: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', color: '#374151' },
    input: { width: '100%', padding: '8px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '7px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
};

export default CarouselComponent;